/**
 * Counter alerts for new pre-orders: a chime made with Web Audio (no sound file), the sound and
 * keep-screen-on preferences for this device, and the screen wake lock.
 *
 * Browsers only let a page play sound after someone has tapped or clicked it, so the audio is unlocked on
 * the first interaction and the alert bar asks for a tap when it is still locked (e.g. after a reload).
 */
import { useEffect, useState, useSyncExternalStore } from 'react';

const SOUND_KEY = 'sd.counter.sound';
const AWAKE_KEY = 'sd.counter.awake';

function readPref(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === '1';
  } catch {
    return fallback;
  }
}
function writePref(key: string, value: boolean) {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // Private mode or blocked storage: the preference lasts until the page is closed.
  }
}

let ctx: AudioContext | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function audioContext(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  ctx = new Ctor();
  ctx.onstatechange = emit;
  return ctx;
}

/** Call from a tap or click. Safari only allows starting audio inside the gesture itself. */
export function unlockAudio() {
  const c = audioContext();
  if (!c) return;
  if (c.state !== 'running') void c.resume().then(emit, emit);
  // A silent blip inside the gesture is what finally unlocks audio on older iOS versions.
  const g = c.createGain();
  g.gain.value = 0;
  g.connect(c.destination);
  const o = c.createOscillator();
  o.connect(g);
  o.start();
  o.stop(c.currentTime + 0.01);
}

export function useAudioUnlocked(): boolean {
  const [ok, setOk] = useState(() => ctx?.state === 'running');
  useEffect(() => {
    const update = () => setOk(ctx?.state === 'running');
    listeners.add(update);
    const onGesture = () => unlockAudio();
    window.addEventListener('pointerdown', onGesture);
    window.addEventListener('keydown', onGesture);
    update();
    return () => {
      listeners.delete(update);
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
    };
  }, []);
  return ok;
}

/** A bright three-note chime, about a second long. Plays nothing if audio is still locked. */
export function playChime() {
  const c = audioContext();
  if (!c || c.state !== 'running') return;
  const start = c.currentTime + 0.02;
  [880, 1175, 1568].forEach((freq, i) => {
    const t = start + i * 0.16;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.45, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
    osc.connect(gain);
    gain.connect(c.destination);
    osc.start(t);
    osc.stop(t + 0.55);
  });
}

const prefs = { sound: readPref(SOUND_KEY, true), awake: readPref(AWAKE_KEY, false) };
const prefListeners = new Set<() => void>();

/** A device preference shared by every component that reads it. */
export function usePref(key: keyof typeof prefs): [boolean, (v: boolean) => void] {
  const value = useSyncExternalStore(
    (l) => {
      prefListeners.add(l);
      return () => prefListeners.delete(l);
    },
    () => prefs[key],
  );
  return [
    value,
    (v: boolean) => {
      prefs[key] = v;
      writePref(key === 'sound' ? SOUND_KEY : AWAKE_KEY, v);
      prefListeners.forEach((l) => l());
    },
  ];
}

/** Keeps the screen on while enabled and the page is visible (tablets at the counter). Needs https or localhost. */
export function useWakeLock(enabled: boolean): { supported: boolean } {
  const supported = typeof navigator !== 'undefined' && 'wakeLock' in navigator && window.isSecureContext;
  useEffect(() => {
    if (!enabled || !supported) return;
    let lock: WakeLockSentinel | null = null;
    let cancelled = false;
    const acquire = async () => {
      if (document.visibilityState !== 'visible' || lock) return;
      try {
        const l = await navigator.wakeLock.request('screen');
        if (cancelled) {
          void l.release();
          return;
        }
        lock = l;
        l.addEventListener('release', () => {
          if (lock === l) lock = null;
        });
      } catch {
        // Battery saver or the browser refused; try again next time the page becomes visible.
      }
    };
    void acquire();
    const onVisible = () => void acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      void lock?.release();
    };
  }, [enabled, supported]);
  return { supported };
}
